import { useState, useEffect, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TaskList, TaskItem } from "@tiptap/extension-list";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { EmoticonReplacer } from "../../../utils/emoticonExtension";
import { fetcher } from "../../../utils/fetcher";
import { useResync } from "../../../hooks/useRealtime";
import { useAutosave } from "../../../hooks/useAutosave";
import FlowTextInput from "../../FlowTextInput/FlowTextInput";
import FlowButton from "../../FlowButton/FlowButton";
import Spinner from "../../Spinner/Spinner";
import Modal from "../../Modal/Modal";
import TextColorPicker from "../../../pages/Notes/Modals/TextColorPicker/TextColorPicker";
import EmojiPickerModal from "../../../pages/Notes/Modals/EmojiPickerModal/EmojiPickerModal";
import useEditorActiveFormats from "../../../hooks/useEditorActiveFormats";
import "./NoteEditorModal.scss";
import { useTranslation } from "react-i18next";

function NoteEditorModal({ noteId, onNoteUpdated }) {
  const { t } = useTranslation();
  const [note, setNote] = useState(null);
  const [noteTitle, setNoteTitle] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(true);
  const lastSavedNoteData = useRef({ title: "", content: "" });
  const editorScrollableRef = useRef(null);
  const fileInputRef = useRef(null);
  const pickerModalRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TaskList,
      TaskItem.configure({ nested: true }),
      TextStyle,
      Color,
      EmoticonReplacer,
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: "resizable-image",
        },
      }),
    ],
    content: "",
    editable: true,
    editorProps: {
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find((item) => item.type.startsWith("image/"));

        if (imageItem) {
          event.preventDefault();
          const file = imageItem.getAsFile();
          if (file) {
            handleImageUpload(file);
          }
          return true;
        }
        return false;
      },
      handleDOMEvents: {
        mousedown: (view, event) => {
          const target = event.target;
          if (target.tagName === "IMG") {
            const img = target;
            const rect = img.getBoundingClientRect();
            const cornerSize = 20;

            // Check if clicking in corners
            const isBottomRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isBottomLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isTopRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY < rect.top + cornerSize;

            const isTopLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY < rect.top + cornerSize;

            if (isBottomRight || isBottomLeft || isTopRight || isTopLeft) {
              event.preventDefault();

              const startX = event.clientX;
              const startY = event.clientY;
              const startWidth = img.offsetWidth;
              const aspectRatio = img.naturalHeight / img.naturalWidth;

              const handleMouseMove = (e) => {
                const diffX = e.clientX - startX;
                const diffY = e.clientY - startY;

                // Use the larger absolute difference to maintain aspect ratio
                const diff =
                  Math.abs(diffX) > Math.abs(diffY)
                    ? diffX
                    : diffY / aspectRatio;

                // Apply direction multiplier for left corners
                const directionMultiplier = isBottomLeft || isTopLeft ? -1 : 1;

                const newWidth = Math.max(
                  100,
                  Math.min(
                    startWidth + diff * directionMultiplier,
                    view.dom.offsetWidth - 40,
                  ),
                );

                img.style.width = newWidth + "px";
                img.style.height = "auto";
              };

              const handleMouseUp = () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
                document.body.style.cursor = "";

                // Update the image node with new width
                const pos = view.posAtDOM(img, 0);
                if (pos !== null && pos !== undefined) {
                  const node = view.state.doc.nodeAt(pos);
                  if (node && node.type.name === "image") {
                    view.dispatch(
                      view.state.tr.setNodeMarkup(pos, null, {
                        ...node.attrs,
                        width: img.style.width,
                      }),
                    );
                  }
                }
              };

              // Set appropriate cursor
              if (isBottomRight || isTopLeft) {
                document.body.style.cursor = "nwse-resize";
              } else {
                document.body.style.cursor = "nesw-resize";
              }

              document.addEventListener("mousemove", handleMouseMove);
              document.addEventListener("mouseup", handleMouseUp);
            }
          }
          return false;
        },
        mousemove: (view, event) => {
          const target = event.target;
          if (target.tagName === "IMG") {
            const img = target;
            const rect = img.getBoundingClientRect();
            const cornerSize = 20;

            // Update cursor based on position
            const isBottomRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isBottomLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY > rect.bottom - cornerSize;

            const isTopRight =
              event.clientX > rect.right - cornerSize &&
              event.clientY < rect.top + cornerSize;

            const isTopLeft =
              event.clientX < rect.left + cornerSize &&
              event.clientY < rect.top + cornerSize;

            if (isBottomRight || isTopLeft) {
              img.style.cursor = "nwse-resize";
            } else if (isBottomLeft || isTopRight) {
              img.style.cursor = "nesw-resize";
            } else {
              img.style.cursor = "default";
            }
          }
          return false;
        },
      },
    },
  });
  const activeFormats = useEditorActiveFormats(editor);

  const handleImageUpload = async (file) => {
    if (!note || !editor) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert(t("tasks.images.tooLarge"));
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert(t("tasks.links.onlyImages"));
      return;
    }

    setIsUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetcher.post(
        `/api/notes/${note._id}/upload-image`,
        formData,
      );

      if (response.success && response.image) {
        // Insert the thumbnail image into the editor with alt text
        editor
          .chain()
          .focus()
          .setImage({
            src: response.image.thumbnailUrl,
            alt: response.image.filename,
          })
          .run();
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      alert(t("tasks.images.uploadFailed"));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const openPicker = (e, content) => {
    pickerModalRef.current?.open({
      arrow: "top-center",
      content,
      triggerElement: e.currentTarget,
    });
  };

  const closePicker = () => {
    pickerModalRef.current?.close();
  };

  const handleTextColorPicker = (e) => {
    openPicker(
      e,
      <TextColorPicker
        onSelectColor={(color) => {
          editor?.chain().focus().setColor(color).run();
          closePicker();
        }}
      />,
    );
  };

  const handleEmojiPicker = (e) => {
    openPicker(
      e,
      <EmojiPickerModal
        onSelectEmoji={(emoji) => {
          editor?.commands.insertContent(emoji);
          closePicker();
        }}
      />,
    );
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const handleUpdateNote = useCallback(
    async (isBackground = false) => {
      if (!note || !editor) return;

      const updatedTitle = noteTitle.trim();
      const updatedContent = editor.getHTML();

      if (isBackground) {
        // Background save: no waiting, errors are only logged
        return fetcher
          .put(`/api/notes/${note._id}`, {
            rev: note.rev,
            title: noteTitle,
            content: updatedContent,
          })
          .then(() => {
            lastSavedNoteData.current = {
              title: updatedTitle,
              content: updatedContent,
            };
            if (onNoteUpdated) {
              onNoteUpdated({
                ...note,
                title: updatedTitle,
                content: updatedContent,
              });
            }
          })
          .catch((err) => {
            console.error("Error updating note (background):", err);
          });
      }

      // Manual save: show feedback and wait for response
      setIsUploading(true);
      const minimumSpinTime = new Promise((resolve) =>
        setTimeout(resolve, 1000),
      );

      try {
        await fetcher.put(`/api/notes/${note._id}`, {
          rev: note.rev,
          title: noteTitle,
          content: updatedContent,
        });

        lastSavedNoteData.current = {
          title: updatedTitle,
          content: updatedContent,
        };

        if (onNoteUpdated) {
          onNoteUpdated({
            ...note,
            title: updatedTitle,
            content: updatedContent,
          });
        }
      } catch (err) {
        console.error("Error updating note:", err);
      } finally {
        await minimumSpinTime;
        setIsUploading(false);
      }
    },
    [note, editor, noteTitle, onNoteUpdated],
  );

  // Fetch the note data
  const loadNote = useCallback(
    (showLoading = true) => {
      if (!noteId) return;

      if (showLoading) setIsLoading(true);
      fetcher
        .get(`/api/notes/${noteId}`)
        .then((data) => {
          setNote(data);
          setNoteTitle(data.title || "");
          lastSavedNoteData.current = {
            title: data.title?.trim() || "",
            content: data.content || "",
          };
          if (editor) {
            editor.commands.setContent(data.content || "");
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching note:", err);
          setIsLoading(false);
        });
    },
    [noteId, editor],
  );

  useEffect(() => {
    loadNote();
  }, [loadNote]);

  // Reload when the note may have been changed on another device. Unsaved
  // changes are kept, unless the server rejected them as outdated.
  useResync((reason, conflictId) => {
    const hasUnsavedChanges =
      editor &&
      (noteTitle.trim() !== lastSavedNoteData.current.title ||
        editor.getHTML() !== lastSavedNoteData.current.content);
    const wasRejected = reason === "conflict" && conflictId === noteId;
    if (hasUnsavedChanges && !wasRejected) return;
    loadNote(false);
  });

  // Auto-save once the user has paused typing
  const { scheduleSave, isAutosaving } = useAutosave({
    enabled: autoUpdateEnabled && !!note,
    hasChanges: () =>
      !!editor &&
      (noteTitle.trim() !== lastSavedNoteData.current.title ||
        editor.getHTML() !== lastSavedNoteData.current.content),
    save: () => handleUpdateNote(true),
  });

  useEffect(() => {
    if (!editor) return;
    editor.on("update", scheduleSave);
    return () => editor.off("update", scheduleSave);
  }, [editor, scheduleSave]);

  useEffect(() => {
    scheduleSave();
  }, [noteTitle, scheduleSave]);

  if (isLoading) {
    return (
      <div className="note-editor-modal">
        <Spinner />
      </div>
    );
  }

  if (!note) {
    return (
      <div className="note-editor-modal">
        <div className="empty-state">{t("tasks.links.noteNotFound")}</div>
      </div>
    );
  }

  return (
    <div className="note-editor-modal">
      <div className="note-title-bar">
        <FlowTextInput
          className="note-title-flow-input"
          placeholder={t("tasks.links.noteTitlePlaceholder")}
          value={noteTitle}
          onChange={(value) => setNoteTitle(value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleUpdateNote();
            }
          }}
          expandable={false}
          icon=""
        />
        <FlowButton variant="update" onClick={handleUpdateNote}>
          <span style={{ position: "relative", top: "1px" }}>
            {t("common.save")}
          </span>
          {autoUpdateEnabled && (
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: "16px",
                marginLeft: "4px",
                verticalAlign: "middle",
                animation:
                  isUploading || isAutosaving
                    ? "spin 1s ease-in-out infinite"
                    : "none",
              }}
            >
              cached
            </span>
          )}
        </FlowButton>
        <label className="auto-save-label">
          <input
            type="checkbox"
            checked={autoUpdateEnabled}
            onChange={(e) => setAutoUpdateEnabled(e.target.checked)}
          />
          {t("tasks.links.autoSave")}
        </label>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <div className="note-editor-wrapper">
        <div className="note-editor-controls">
          <button
            className={`editor-btn ${activeFormats.bold ? "is-active" : ""}`}
            onClick={() => editor?.chain().focus().toggleBold().run()}
          >
            <span className="editor-btn-text">B</span>
          </button>
          <button
            className={`editor-btn ${activeFormats.italic ? "is-active" : ""}`}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          >
            <span className="editor-btn-text" style={{ fontStyle: "italic" }}>
              I
            </span>
          </button>
          <button
            className={`editor-btn ${activeFormats.strike ? "is-active" : ""}`}
            onClick={() => editor?.chain().focus().toggleStrike().run()}
          >
            <span
              className="editor-btn-text"
              style={{ textDecoration: "line-through" }}
            >
              S
            </span>
          </button>
          <span className="editor-separator" />
          <button
            className={`editor-btn ${activeFormats.h1 ? "is-active" : ""}`}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 1 }).run()
            }
          >
            <span className="editor-btn-text">H1</span>
          </button>
          <button
            className={`editor-btn ${activeFormats.h2 ? "is-active" : ""}`}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            <span className="editor-btn-text">H2</span>
          </button>
          <button
            className={`editor-btn ${activeFormats.h3 ? "is-active" : ""}`}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 3 }).run()
            }
          >
            <span className="editor-btn-text">H3</span>
          </button>
          <span className="editor-separator" />
          <button
            className="editor-btn"
            onClick={handleTextColorPicker}
            title={t("tasks.links.textColor")}
          >
            <span className="material-symbols-outlined">format_color_text</span>
          </button>
          <button
            className="editor-btn"
            onClick={handleEmojiPicker}
            title={t("tasks.links.insertEmoji")}
          >
            <span className="material-symbols-outlined">mood</span>
          </button>
          <span className="editor-separator" />
          <button
            className={`editor-btn ${activeFormats.bulletList ? "is-active" : ""}`}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
          >
            <span className="material-symbols-outlined">
              format_list_bulleted
            </span>
          </button>
          <button
            className={`editor-btn ${activeFormats.orderedList ? "is-active" : ""}`}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          >
            <span className="material-symbols-outlined">
              format_list_numbered
            </span>
          </button>
          <button
            className={`editor-btn ${activeFormats.taskList ? "is-active" : ""}`}
            title={t("tasks.links.checklist")}
            onClick={() => editor?.chain().focus().toggleTaskList().run()}
          >
            <span className="material-symbols-outlined">checklist</span>
          </button>
          <span className="editor-separator" />
          <button
            className={`editor-btn ${activeFormats.blockquote ? "is-active" : ""}`}
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
          >
            <span className="material-symbols-outlined">format_quote</span>
          </button>
          <button
            className={`editor-btn ${activeFormats.codeBlock ? "is-active" : ""}`}
            onClick={() => {
              if (!editor) return;

              const { from, to } = editor.state.selection;
              const selectedText = editor.state.doc.textBetween(from, to, "\n");

              // If we have selected text across multiple paragraphs
              if (selectedText && selectedText.includes("\n")) {
                editor
                  .chain()
                  .focus()
                  .deleteSelection()
                  .insertContent({
                    type: "codeBlock",
                    content: [{ type: "text", text: selectedText }],
                  })
                  .run();
              } else {
                // Normal toggle for single line or empty
                editor.chain().focus().toggleCodeBlock().run();
              }
            }}
          >
            <span className="material-symbols-outlined">code</span>
          </button>
          <span className="editor-separator" />
          <button
            className="editor-btn"
            onClick={handleImageButtonClick}
            disabled={isUploadingImage}
            title={t("tasks.images.uploadImage")}
          >
            <span className="material-symbols-outlined">
              {isUploadingImage ? "hourglass_empty" : "image"}
            </span>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
        </div>
        <div
          ref={editorScrollableRef}
          className="note-editor-scrollable"
          onClick={() => {
            if (editor && !editor.isFocused) {
              editor.commands.focus("end");
            }
          }}
        >
          <EditorContent editor={editor} className="note-content-area" />
        </div>
      </div>
      <Modal ref={pickerModalRef} />
    </div>
  );
}

export default NoteEditorModal;
